<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">設定</h2>
        <button @click="closeModal" class="close-button">×</button>
      </div>

      <div class="modal-body">
        <!-- メインビューの表示 -->
        <div v-if="currentView === 'main'" class="settings-menu">
          <button @click="navigateTo('account')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="user-solid-full" size="sm" />
            </span>
            <span class="menu-text">アカウント編集</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('login')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="key-solid-full" size="sm" />
            </span>
            <span class="menu-text">ログイン設定</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('about')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="circle-info-solid-full" size="sm" />
            </span>
            <span class="menu-text">アプリについて</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('terms')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="file-contract-solid-full" size="sm" />
            </span>
            <span class="menu-text">利用規約</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('privacy')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="key-solid-full" size="sm" />
            </span>
            <span class="menu-text">プライバシーポリシー</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('contact')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="envelope-solid-full" size="sm" />
            </span>
            <span class="menu-text">お問い合わせ</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="navigateTo('faq')" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="question-solid-full" size="sm" />
            </span>
            <span class="menu-text">Q&A</span>
            <span class="menu-arrow">›</span>
          </button>

          <button @click="openChecklistTemplate" class="menu-item">
            <span class="menu-icon">
              <IconSvg name="bag-shopping-solid-full" size="sm" />
            </span>
            <span class="menu-text">持ち物テンプレート</span>
            <span class="menu-arrow">›</span>
          </button>

          <div class="menu-divider"></div>

          <button @click="handleLogout" class="menu-item danger">
            <span class="menu-icon">
              <IconSvg name="arrow-right-from-bracket-solid-full" size="sm" />
            </span>
            <span class="menu-text">ログアウト</span>
          </button>

          <button
            @click="navigateTo('delete-account')"
            class="menu-item danger"
          >
            <span class="menu-icon">
              <IconSvg name="triangle-exclamation-solid-full" size="sm" />
            </span>
            <span class="menu-text">アカウントを削除</span>
          </button>
        </div>

        <!-- アカウント編集 -->
        <div v-else-if="currentView === 'account'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">アカウント編集</h3>

          <!-- プロフィール画像 -->
          <div class="form-group">
            <label class="form-label">プロフィール画像</label>
            <div class="profile-image-container">
              <div class="profile-image-preview">
                <img
                  v-if="accountForm.profileImagePreview"
                  :src="accountForm.profileImagePreview"
                  alt="プロフィール画像"
                  class="profile-preview-img"
                  loading="lazy"
                  decoding="async"
                />
                <div v-else class="profile-preview-placeholder">
                  <IconSvg name="user-solid-full" size="lg" />
                </div>
              </div>
              <div class="profile-image-actions">
                <input
                  type="file"
                  ref="profileImageInput"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif,.hif"
                  @change="handleProfileImageChange"
                  style="display: none"
                />
                <button
                  type="button"
                  @click="profileImageInput.click()"
                  class="btn btn-secondary"
                >
                  画像を選択
                </button>
                <button
                  v-if="accountForm.profileImagePreview"
                  type="button"
                  @click="clearProfileImage"
                  class="btn btn-secondary"
                  :disabled="isSubmitting"
                >
                  {{ isSubmitting ? "削除中..." : "削除" }}
                </button>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">ユーザー名</label>
            <input
              v-model="accountForm.userName"
              type="text"
              class="form-input"
              placeholder="ユーザー名を入力"
            />
          </div>

          <!-- コメント -->
          <div class="form-group">
            <label class="form-label">コメント</label>
            <textarea
              v-model="accountForm.comment"
              class="form-textarea"
              placeholder="自己紹介やコメントを入力"
              rows="4"
              maxlength="500"
            ></textarea>
            <div class="character-count">
              {{ accountForm.comment?.length || 0 }} / 500
            </div>
          </div>

          <button
            @click="updateAccount"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "保存中..." : "保存" }}
          </button>

          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- ログイン設定 -->
        <div v-else-if="currentView === 'login'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">ログイン設定</h3>

          <button @click="navigateTo('change-email')" class="menu-item">
            <span class="menu-text">メールアドレスの変更</span>
            <span class="menu-arrow">›</span>
          </button>

          <button
            v-if="!isGoogleAccount"
            @click="navigateTo('change-password')"
            class="menu-item"
          >
            <span class="menu-text">パスワードの変更</span>
            <span class="menu-arrow">›</span>
          </button>
        </div>

        <!-- メールアドレスの変更 -->
        <div v-else-if="currentView === 'change-email'" class="settings-detail">
          <button @click="navigateTo('login')" class="back-button">
            ‹ 戻る
          </button>
          <h3 class="detail-title">メールアドレスの変更</h3>

          <div class="form-group">
            <label class="form-label">現在のメールアドレス</label>
            <input
              type="email"
              class="form-input"
              :value="authStore.user?.EMAIL || authStore.user?.email"
              disabled
            />
          </div>

          <div class="form-group">
            <label class="form-label">新しいメールアドレス</label>
            <input
              v-model="emailForm.newEmail"
              type="email"
              class="form-input"
              placeholder="新しいメールアドレスを入力"
            />
          </div>

          <div class="form-group">
            <label class="form-label">パスワード</label>
            <div class="password-input-wrapper">
              <input
                v-model="emailForm.password"
                :type="showEmailPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="現在のパスワードを入力"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showEmailPassword = !showEmailPassword"
                :aria-label="
                  showEmailPassword ? 'パスワードを隠す' : 'パスワードを表示'
                "
              >
                <svg
                  v-if="!showEmailPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
            <a
              v-if="!isGoogleAccount"
              href="#"
              class="forgot-password-link"
              @click.prevent="navigateTo('reset-password')"
              >パスワードを忘れた場合</a
            >
          </div>

          <button
            @click="updateEmail"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "変更中..." : "変更" }}
          </button>

          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- パスワードの変更 -->
        <div
          v-else-if="currentView === 'change-password'"
          class="settings-detail"
        >
          <button @click="navigateTo('login')" class="back-button">
            ‹ 戻る
          </button>
          <h3 class="detail-title">パスワードの変更</h3>

          <div class="form-group">
            <label class="form-label">現在のパスワード</label>
            <div class="password-input-wrapper">
              <input
                v-model="passwordForm.currentPassword"
                :type="showCurrentPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="現在のパスワードを入力"
                autocomplete="current-password"
                @paste.prevent
                @copy.prevent
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showCurrentPassword = !showCurrentPassword"
                :aria-label="
                  showCurrentPassword ? 'パスワードを隠す' : 'パスワードを表示'
                "
              >
                <svg
                  v-if="!showCurrentPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
            <a
              v-if="!isGoogleAccount"
              href="#"
              class="forgot-password-link"
              @click.prevent="navigateTo('reset-password')"
              >パスワードを忘れた場合</a
            >
          </div>

          <div class="form-group">
            <label class="form-label">新しいパスワード</label>
            <div class="password-input-wrapper">
              <input
                v-model="passwordForm.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="新しいパスワードを入力"
                autocomplete="new-password"
                @paste.prevent
                @copy.prevent
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showNewPassword = !showNewPassword"
                :aria-label="
                  showNewPassword ? 'パスワードを隠す' : 'パスワードを表示'
                "
              >
                <svg
                  v-if="!showNewPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">新しいパスワード（確認）</label>
            <div class="password-input-wrapper">
              <input
                v-model="passwordForm.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                placeholder="新しいパスワードを再入力"
                autocomplete="new-password"
                @paste.prevent
                @copy.prevent
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showConfirmPassword = !showConfirmPassword"
                :aria-label="
                  showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'
                "
              >
                <svg
                  v-if="!showConfirmPassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <button
            @click="updatePassword"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "変更中..." : "変更" }}
          </button>

          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- パスワードリセット -->
        <div
          v-else-if="currentView === 'reset-password'"
          class="settings-detail"
        >
          <button @click="navigateTo('login')" class="back-button">
            ‹ 戻る
          </button>
          <h3 class="detail-title">パスワードのリセット</h3>

          <p class="info-text">
            登録されたメールアドレスにパスワードリセット用のリンクを送信します。
          </p>

          <div class="form-group">
            <label class="form-label">メールアドレス</label>
            <input
              v-model="resetPasswordForm.email"
              type="email"
              class="form-input"
              placeholder="登録されたメールアドレスを入力"
              autocomplete="email"
            />
          </div>

          <button
            @click="requestPasswordReset"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "送信中..." : "リセットリンクを送信" }}
          </button>

          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- バックアップ方法 -->
        <!-- アプリについて -->
        <div v-else-if="currentView === 'about'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">アプリについて</h3>

          <div class="about-section">
            <div class="about-item">
              <span class="about-label">バージョン</span>
              <span class="about-value">1.0.0</span>
            </div>
          </div>

        </div>

        <!-- 利用規約 -->
        <div v-else-if="currentView === 'terms'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">利用規約</h3>
          <div v-if="termsContent" class="content-box terms-content">
            <p style="white-space: pre-wrap">{{ termsContent }}</p>
          </div>
          <p v-else class="info-text">利用規約は現在準備中です。</p>
        </div>

        <!-- プライバシーポリシー -->
        <div v-else-if="currentView === 'privacy'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">プライバシーポリシー</h3>
          <div v-if="privacyContent" class="content-box terms-content">
            <p style="white-space: pre-wrap">{{ privacyContent }}</p>
          </div>
          <p v-else class="info-text">プライバシーポリシーは現在準備中です。</p>
        </div>

        <!-- お問い合わせ -->
        <div v-else-if="currentView === 'contact'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">お問い合わせ</h3>

          <div class="form-group">
            <label class="form-label">件名</label>
            <input
              v-model="contactForm.subject"
              type="text"
              class="form-input"
              placeholder="お問い合わせの件名"
            />
          </div>

          <div class="form-group">
            <label class="form-label">内容</label>
            <textarea
              v-model="contactForm.message"
              class="form-textarea"
              rows="6"
              placeholder="お問い合わせ内容を入力してください"
            ></textarea>
          </div>

          <button
            @click="sendContact"
            class="btn btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "送信中..." : "送信" }}
          </button>

          <p v-if="successMessage" class="success-text">{{ successMessage }}</p>
          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- Q&A -->
        <div v-else-if="currentView === 'faq'" class="settings-detail">
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">Q&A</h3>

          <div v-if="faqs.length" class="faq-list">
            <div
              v-for="(faq, index) in faqs"
              :key="faq.QA_NO"
              class="faq-item"
              style="cursor: pointer"
              @click="toggleFaqItem(index)"
            >
              <h4 class="faq-question">{{ faq.QUESTION_TEXT }}</h4>
              <p v-if="openFaqIndex === index" class="faq-answer">
                {{ faq.ANSWER_TEXT }}
              </p>
            </div>
          </div>
          <p v-else class="info-text">Q&Aは現在準備中です。</p>
        </div>

        <!-- アカウント削除確認 -->
        <div
          v-else-if="currentView === 'delete-account'"
          class="settings-detail"
        >
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">アカウントを削除</h3>

          <div class="warning-box">
            <p>
              <IconSvg name="triangle-exclamation-solid-full" size="sm" />
              アカウントを削除すると、すべてのデータが削除され、元に戻すことはできません。
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">パスワード（確認用）</label>
            <div class="password-input-wrapper">
              <input
                v-model="deleteForm.password"
                :type="showDeletePassword ? 'text' : 'password'"
                class="form-input"
                placeholder="パスワードを入力"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="showDeletePassword = !showDeletePassword"
                :aria-label="
                  showDeletePassword ? 'パスワードを隠す' : 'パスワードを表示'
                "
              >
                <svg
                  v-if="!showDeletePassword"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                  ></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>

          <button
            @click="deleteAccount"
            class="btn btn-danger"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? "削除中..." : "アカウントを削除" }}
          </button>

          <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
        </div>

        <!-- 持ち物テンプレート管理 -->
        <div
          v-else-if="currentView === 'checklist-template'"
          class="settings-detail"
        >
          <button @click="backToMain" class="back-button">‹ 戻る</button>
          <h3 class="detail-title">持ち物テンプレート</h3>
          <p class="info-text">
            テンプレートに登録した持ち物は、各しおりの持ち物リストにワンタップでコピーできます。
          </p>

          <!-- テンプレートアイテム追加フォーム -->
          <div class="form-group">
            <label class="form-label">カテゴリ名</label>
            <div class="combobox-wrapper">
              <input
                type="text"
                class="form-input"
                v-model="templateForm.categoryName"
                placeholder="例: 衣類、洗面用具（省略可）"
                maxlength="255"
                autocomplete="off"
                @focus="showTemplateCategoryDropdown = true"
                @blur="hideTemplateCategoryDropdown"
              />
              <ul v-if="showTemplateCategoryDropdown && filteredTemplateCategories.length > 0" class="combobox-dropdown">
                <li
                  v-for="cat in filteredTemplateCategories"
                  :key="cat"
                  @mousedown.prevent="selectTemplateCategory(cat)"
                >{{ cat }}</li>
              </ul>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">アイテム名</label>
            <div class="template-add-row">
              <input
                v-model="templateForm.itemName"
                type="text"
                class="form-input"
                placeholder="例: 財布、パスポート"
                maxlength="255"
                @keyup.enter="submitTemplateItem"
              />
              <button
                class="btn btn-primary template-add-btn"
                @click="submitTemplateItem"
                :disabled="templateSubmitting"
              >
                {{ templateEditId ? "更新" : "追加" }}
              </button>
            </div>
            <button
              v-if="templateEditId"
              class="btn btn-secondary template-cancel-btn"
              @click="cancelTemplateEdit"
            >
              キャンセル
            </button>
          </div>

          <p v-if="templateError" class="error-text">{{ templateError }}</p>
          <p v-if="templateSuccess" class="success-text">
            {{ templateSuccess }}
          </p>

          <!-- テンプレートアイテム一覧 -->
          <div v-if="templateStore.loading" class="loading">読み込み中...</div>
          <div v-else-if="templateStore.items.length === 0" class="empty">
            <p>
              テンプレートにアイテムがありません。<br />上のフォームから追加してください。
            </p>
          </div>
          <div v-else class="template-list">
            <div
              v-for="(group, catName) in groupedTemplateItems"
              :key="catName"
              class="template-category"
            >
              <div class="template-category-name">{{ catName }}</div>
              <div
                v-for="item in group"
                :key="item.TEMPLATE_NO"
                class="template-item"
              >
                <span class="template-item-name">{{ item.ITEM_NAME }}</span>
                <div class="template-item-actions">
                  <button
                    class="icon-btn"
                    @click="startEditTemplate(item)"
                    title="編集"
                  >
                    ✎
                  </button>
                  <button
                    class="icon-btn danger"
                    @click="removeTemplateItem(item.TEMPLATE_NO)"
                    title="削除"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/authStore.js";
import { useChecklistTemplateStore } from "@/store/checklist-template-store.js";
import IconSvg from "@/components/IconSvg.vue";
import axios from "axios";
import { API_URL } from "@/config/api.js";
import { deleteProfileImage } from "@/api/auth.js";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  initialView: {
    type: String,
    default: "main",
  },
});

const emit = defineEmits(["update:modelValue"]);

const router = useRouter();
const authStore = useAuthStore();
const templateStore = useChecklistTemplateStore();

const currentView = ref("main");
const isSubmitting = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const profileImageInput = ref(null);

// フォームデータ
const accountForm = ref({
  userName: "",
  comment: "",
  profileImage: null,
  profileImagePreview: null,
});

const emailForm = ref({
  newEmail: "",
  password: "",
});

const passwordForm = ref({
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});

const passwordFailCount = ref(0);
const passwordLocked = ref(false);

const resetPasswordForm = ref({
  email: "",
});

const isGoogleAccount = computed(
  () => authStore.user?.AUTH_PROVIDER === "google",
);

const contactForm = ref({
  subject: "",
  message: "",
});

// DB取得データ
const faqs = ref([]);
const termsContent = ref(null);
const privacyContent = ref(null);
const openFaqIndex = ref(null);

onMounted(async () => {
  try {
    const [faqRes, termsRes, privacyRes] = await Promise.all([
      axios.get(`${API_URL}/qa`),
      axios.get(`${API_URL}/terms/active`),
      axios.get(`${API_URL}/privacy/active`),
    ]);
    faqs.value = faqRes.data;
    termsContent.value = termsRes.data?.CONTENT_TEXT || null;
    privacyContent.value = privacyRes.data?.CONTENT_TEXT || null;
  } catch (e) {
    console.error("設定データ取得エラー:", e);
  }
});

const toggleFaqItem = (index) => {
  openFaqIndex.value = openFaqIndex.value === index ? null : index;
};

const deleteForm = ref({
  password: "",
});

// 持ち物テンプレート
const templateForm = ref({ itemName: "", categoryName: "" });
const showTemplateCategoryDropdown = ref(false);

const filteredTemplateCategories = computed(() => {
  const existing = Object.keys(groupedTemplateItems.value);
  const input = (templateForm.value.categoryName || '').trim().toLowerCase();
  if (!input) return existing;
  return existing.filter(cat => cat.toLowerCase().includes(input));
});

const selectTemplateCategory = (cat) => {
  templateForm.value.categoryName = cat;
  showTemplateCategoryDropdown.value = false;
};

const hideTemplateCategoryDropdown = () => {
  setTimeout(() => { showTemplateCategoryDropdown.value = false; }, 150);
};

const templateEditId = ref(null);
const templateSubmitting = ref(false);
const templateError = ref("");
const templateSuccess = ref("");

const groupedTemplateItems = computed(() => {
  const grouped = {};
  for (const item of templateStore.items) {
    const cat = item.CATEGORY_NAME || "その他";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }
  return grouped;
});

const openChecklistTemplate = async () => {
  navigateTo("checklist-template");
  await templateStore.fetchTemplateItems();
};

const submitTemplateItem = async () => {
  templateError.value = "";
  templateSuccess.value = "";
  const name = templateForm.value.itemName.trim();
  if (!name) {
    templateError.value = "アイテム名を入力してください";
    return;
  }
  templateSubmitting.value = true;
  try {
    if (templateEditId.value) {
      await templateStore.updateTemplateItem(
        templateEditId.value,
        name,
        templateForm.value.categoryName,
      );
      templateSuccess.value = "更新しました";
      templateEditId.value = null;
    } else {
      await templateStore.createTemplateItem(
        name,
        templateForm.value.categoryName,
      );
      templateSuccess.value = "追加しました";
    }
    templateForm.value = { itemName: "", categoryName: "" };
    showTemplateCategoryDropdown.value = false;
  } catch {
    templateError.value = "操作に失敗しました";
  } finally {
    templateSubmitting.value = false;
  }
};

const startEditTemplate = (item) => {
  templateEditId.value = item.TEMPLATE_NO;
  templateForm.value = {
    itemName: item.ITEM_NAME,
    categoryName: item.CATEGORY_NAME || "",
  };
  templateError.value = "";
  templateSuccess.value = "";
};

const cancelTemplateEdit = () => {
  templateEditId.value = null;
  templateForm.value = { itemName: "", categoryName: "" };
  showTemplateCategoryDropdown.value = false;
  templateError.value = "";
  templateSuccess.value = "";
};

const removeTemplateItem = async (templateNo) => {
  if (!confirm("このアイテムを削除してもよろしいですか？")) return;
  templateError.value = "";
  templateSuccess.value = "";
  try {
    await templateStore.deleteTemplateItem(templateNo);
    templateSuccess.value = "削除しました";
  } catch {
    templateError.value = "削除に失敗しました";
  }
};

// パスワード表示切り替え
const showEmailPassword = ref(false);
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const showDeletePassword = ref(false);

// モーダルを閉じる
const closeModal = () => {
  emit("update:modelValue", false);
  // 閉じた時にメインビューに戻す
  setTimeout(() => {
    currentView.value = "main";
    clearMessages();
  }, 300);
};

// ビューを切り替える
const navigateTo = (view) => {
  if (
    isGoogleAccount.value &&
    (view === "change-password" || view === "reset-password")
  ) {
    return;
  }
  currentView.value = view;
  clearMessages();
};

// メインビューに戻る
const backToMain = () => {
  currentView.value = "main";
  clearMessages();
};

// メッセージをクリア
const clearMessages = () => {
  successMessage.value = "";
  errorMessage.value = "";
};

// プロフィール画像の変更ハンドラー
const handleProfileImageChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  clearMessages();

  // 許可する画像形式
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/heic",
    "image/heif",
    "image/heic-sequence",
    "image/heif-sequence",
  ];
  const allowedExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".heic",
    ".heif",
    ".hif",
  ];

  // ファイル名から拡張子を取得
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  // iOSではHEICのMIMEタイプが空になる場合があるため、拡張子またはMIMEで判定
  if (
    !allowedExtensions.includes(fileExtension) &&
    !allowedTypes.includes(file.type.toLowerCase())
  ) {
    errorMessage.value = `対応していない画像形式です。使用可能な形式: JPG, JPEG, PNG, GIF, WEBP, HEIC, HEIF`;
    event.target.value = ""; // ファイル選択をリセット
    return;
  }

  // ファイルサイズチェック（5MB以下）
  const maxSizeInMB = 5;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    errorMessage.value = `画像サイズが大きすぎます（${fileSizeInMB}MB）。${maxSizeInMB}MB以下の画像を選択してください。`;
    event.target.value = ""; // ファイル選択をリセット
    return;
  }

  // 画像の読み込み
  const reader = new FileReader();
  reader.onload = (e) => {
    accountForm.value.profileImage = e.target.result;
    accountForm.value.profileImagePreview = e.target.result;
  };
  reader.onerror = () => {
    errorMessage.value = "画像の読み込みに失敗しました";
    event.target.value = "";
  };
  reader.readAsDataURL(file);
};

// プロフィール画像のクリア
const clearProfileImage = async () => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  clearMessages();

  try {
    await deleteProfileImage();
    accountForm.value.profileImage = null;
    accountForm.value.profileImagePreview = null;
    if (profileImageInput.value) {
      profileImageInput.value.value = "";
    }
    if (authStore.user) {
      authStore.user = {
        ...authStore.user,
        HAS_PROFILE_IMAGE: false,
        PROFILE_IMAGE_BASE64: null,
      };
    }
    successMessage.value = "プロフィール画像を削除しました";
  } catch {
    errorMessage.value =
      "プロフィール画像の削除に失敗しました。時間をおいて再度お試しください。";
  } finally {
    isSubmitting.value = false;
  }
};

// アカウント情報を更新
const updateAccount = async () => {
  if (!accountForm.value.userName.trim()) {
    errorMessage.value = "ユーザー名を入力してください";
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    const token = localStorage.getItem("token");
    const updateData = {
      user_name: accountForm.value.userName,
      comment: accountForm.value.comment || "",
    };

    // プロフィール画像が設定されている場合
    if (accountForm.value.profileImage) {
      updateData.profile_image = accountForm.value.profileImage;
    }

    const response = await axios.put(`${API_URL}/users/me`, updateData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    successMessage.value = "アカウント情報を更新しました";
    if (response.data?.user) {
      authStore.user = response.data.user;
    }
    await authStore.fetchUser();
    accountForm.value.profileImagePreview =
      authStore.user?.PROFILE_IMAGE_BASE64 || null;
    accountForm.value.profileImage = null;
  } catch (error) {
    errorMessage.value = "アカウント情報の更新に失敗しました";
    console.error("Account update error:", error);
  } finally {
    isSubmitting.value = false;
  }
};

// メールアドレスを更新
const updateEmail = async () => {
  if (!emailForm.value.newEmail || !emailForm.value.password) {
    errorMessage.value = "すべての項目を入力してください";
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    const token = localStorage.getItem("token");
    console.log("📧 メールアドレス変更リクエスト:", {
      new_email: emailForm.value.newEmail,
      password: "******",
    });

    await axios.put(
      `${API_URL}/users/email`,
      {
        new_email: emailForm.value.newEmail,
        password: emailForm.value.password,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    successMessage.value = "メールアドレスを変更しました";
    emailForm.value = { newEmail: "", password: "" };
    await authStore.fetchUser();
  } catch (error) {
    console.error("Email update error:", error);
    console.error("Error response:", error.response?.data);

    // サーバーからのエラーメッセージを表示
    if (error.response?.data?.detail) {
      errorMessage.value = error.response.data.detail;
    } else {
      errorMessage.value = "メールアドレスの変更に失敗しました";
    }
  } finally {
    isSubmitting.value = false;
  }
};

// パスワードを更新
const updatePassword = async () => {
  // ロックチェック
  if (passwordLocked.value) {
    errorMessage.value =
      "パスワード入力の試行回数が上限に達しました。登録メールアドレスにパスワードリセットのメールを送信済みです。メールをご確認ください。";
    return;
  }

  if (
    !passwordForm.value.currentPassword ||
    !passwordForm.value.newPassword ||
    !passwordForm.value.confirmPassword
  ) {
    errorMessage.value = "すべての項目を入力してください";
    return;
  }

  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    errorMessage.value = "新しいパスワードが一致しません";
    return;
  }

  if (passwordForm.value.newPassword.length < 6) {
    errorMessage.value = "パスワードは6文字以上にしてください";
    return;
  }

  // 現在のパスワードと新しいパスワードが同じかチェック
  if (passwordForm.value.currentPassword === passwordForm.value.newPassword) {
    errorMessage.value = "同じパスワードは設定できません";
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    const token = localStorage.getItem("token");
    await axios.put(
      `${API_URL}/users/password`,
      {
        current_password: passwordForm.value.currentPassword,
        new_password: passwordForm.value.newPassword,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    successMessage.value = "パスワードを変更しました";
    passwordForm.value = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };
    // 成功時は失敗回数をリセット
    passwordFailCount.value = 0;
    passwordLocked.value = false;
  } catch (error) {
    console.error("Password update error:", error);

    // サーバーからのエラーメッセージを使用
    if (error.response?.data?.detail) {
      errorMessage.value = error.response.data.detail;

      // パスワードが違う場合は失敗回数をカウント
      if (error.response.data.detail === "パスワードが違います") {
        passwordFailCount.value++;

        if (passwordFailCount.value >= 5) {
          passwordLocked.value = true;

          // パスワードリセットメールを送信
          try {
            const userEmail = authStore.user?.EMAIL || authStore.user?.email;
            await axios.post(`${API_URL}/auth/password-reset/request`, {
              email: userEmail,
            });

            errorMessage.value =
              "パスワード入力の試行回数が上限に達しました。登録メールアドレスにパスワードリセットのメールを送信しました。メールをご確認ください。";
            console.log(
              "📧 パスワードリセットメールを送信しました:",
              userEmail,
            );
          } catch (resetError) {
            console.error("Password reset email error:", resetError);
            errorMessage.value =
              "パスワード入力の試行回数が上限に達しました。パスワードリセットメールの送信に失敗しました。";
          }

          // パスワードフォームをクリア
          passwordForm.value = {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          };
        } else {
          errorMessage.value = `パスワードが違います (残り${
            5 - passwordFailCount.value
          }回)`;
        }
      }
    } else {
      errorMessage.value = "パスワードの変更に失敗しました";
    }
  } finally {
    isSubmitting.value = false;
  }
};

// お問い合わせを送信
const sendContact = async () => {
  if (!contactForm.value.subject || !contactForm.value.message) {
    errorMessage.value = "すべての項目を入力してください";
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    const token = localStorage.getItem("token");
    await axios.post(`${API_URL}/contact`, contactForm.value, {
      headers: { Authorization: `Bearer ${token}` },
    });

    successMessage.value = "お問い合わせを送信しました";
    contactForm.value = { subject: "", message: "" };
  } catch (error) {
    errorMessage.value = "お問い合わせの送信に失敗しました";
    console.error("Contact send error:", error);
  } finally {
    isSubmitting.value = false;
  }
};

// パスワードリセットリクエスト
const requestPasswordReset = async () => {
  if (!resetPasswordForm.value.email) {
    errorMessage.value = "メールアドレスを入力してください";
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    await axios.post(`${API_URL}/auth/password-reset/request`, {
      email: resetPasswordForm.value.email,
    });

    successMessage.value = "リセットリンクをメールで送信しました";
    resetPasswordForm.value.email = "";
  } catch (error) {
    errorMessage.value = "リセットリンクの送信に失敗しました";
    console.error("Password reset request error:", error);
  } finally {
    isSubmitting.value = false;
  }
};

// アカウントを削除
const deleteAccount = async () => {
  if (!deleteForm.value.password) {
    errorMessage.value = "パスワードを入力してください";
    return;
  }

  if (!confirm("本当にアカウントを削除しますか？この操作は取り消せません。")) {
    return;
  }

  isSubmitting.value = true;
  clearMessages();

  try {
    const token = localStorage.getItem("token");
    await axios.delete(`${API_URL}/users/me`, {
      data: { password: deleteForm.value.password },
      headers: { Authorization: `Bearer ${token}` },
    });

    alert("アカウントを削除しました");
    authStore.logout();
    closeModal();
    router.push("/");
  } catch (error) {
    errorMessage.value = "アカウントの削除に失敗しました";
    console.error("Account delete error:", error);
  } finally {
    isSubmitting.value = false;
  }
};

// ログアウト
const handleLogout = () => {
  authStore.logout();
  closeModal();
  router.push("/");
};

// モーダルが開かれた時にユーザー情報を読み込む
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen) {
      currentView.value = props.initialView || "main";
      if (currentView.value === "checklist-template") {
        await templateStore.fetchTemplateItems();
      }
      // ユーザー情報がない場合は取得
      if (!authStore.user && authStore.token) {
        try {
          await authStore.fetchUser();
        } catch (error) {
          console.error("[SettingsModal] ユーザー情報取得エラー:", error);
        }
      }

      if (authStore.user) {
        accountForm.value.userName = authStore.user.USER_NAME || "";
        accountForm.value.comment = authStore.user.COMMENT_TEXT || "";
        accountForm.value.profileImagePreview =
          authStore.user.PROFILE_IMAGE_BASE64 || null;
        accountForm.value.profileImage = null; // 新規アップロードはクリア
      }
    }
  },
);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: calc(100dvh - 1rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f3f4f6;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

/* メニューリスト */
.settings-menu {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s;
  width: 100%;
}

.menu-item:hover {
  background-color: #f3f4f6;
}

.menu-item.danger {
  color: #dc2626;
}

.menu-item.danger:hover {
  background-color: #fee2e2;
}

.menu-icon {
  font-size: 1.5rem;
}

.menu-text {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
}

.menu-arrow {
  font-size: 1.25rem;
  color: #9ca3af;
}

.menu-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 0.5rem 0;
}

/* 詳細ビュー */
.settings-detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.back-button {
  align-self: flex-start;
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  margin-left: -0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.back-button:hover {
  background-color: #eff6ff;
}

.detail-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

/* フォーム */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.form-input:disabled {
  background-color: #f3f4f6;
  color: #6b7280;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

/* パスワード入力 */
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
  flex: 1;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.password-toggle-btn:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.password-toggle-btn:active {
  transform: scale(0.95);
}

.forgot-password-link {
  font-size: 0.875rem;
  color: #3b82f6;
  text-decoration: none;
  margin-top: 0.25rem;
  display: inline-block;
  transition: color 0.2s;
}

.forgot-password-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.info-text {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
}

/* プロフィール画像 */
.profile-image-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile-image-preview {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
}

.profile-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 3rem;
}

.profile-image-actions {
  display: flex;
  gap: 0.5rem;
}

.character-count {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
}

/* ボタン */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-danger {
  background-color: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #b91c1c;
}

/* メッセージ */
.success-text {
  color: #10b981;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.error-text {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

/* バックアップリスト */
.backup-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.backup-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
}

.backup-name {
  font-weight: 500;
}

.backup-status {
  font-size: 0.875rem;
  color: #6b7280;
}

.backup-status.connected {
  color: #10b981;
}

/* アプリについて */
.about-section {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.about-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.about-label {
  font-weight: 500;
  color: #6b7280;
}

.about-value {
  color: #111827;
}

/* コンテンツボックス */
.content-box {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 1.5rem;
  line-height: 1.6;
}

/* 利用規約コンテンツ */
.terms-content {
  max-height: 60vh;
  overflow-y: auto;
  text-align: left;
}

.terms-intro {
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
}

.terms-heading {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.terms-text {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.terms-list {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.6;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  list-style-type: decimal;
}

.terms-list li {
  margin-bottom: 0.5rem;
  padding-left: 0.25rem;
}

/* 警告ボックス */
.warning-box {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  color: #991b1b;
}

/* FAQ */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
}

.faq-question {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.faq-answer {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

/* 持ち物テンプレート */
/* コンボボックス */
.combobox-wrapper {
  position: relative;
}

.combobox-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 2px;
  max-height: 180px;
  overflow-y: auto;
  list-style: none;
  padding: 0.25rem 0;
  margin-left: 0;
}

.combobox-dropdown li {
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.combobox-dropdown li:hover {
  background: var(--color-background);
  color: var(--primary-color);
}

.template-add-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.template-add-row .form-input {
  flex: 1;
}

.template-add-btn {
  white-space: nowrap;
  flex-shrink: 0;
}

.template-cancel-btn {
  margin-top: 0.5rem;
  width: 100%;
}

.template-list {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.template-category {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.template-category-name {
  background-color: #f3f4f6;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.template-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.template-item-name {
  font-size: 0.95rem;
  color: #374151;
  flex: 1;
}

.template-item-actions {
  display: flex;
  gap: 0.375rem;
}

.icon-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.2rem 0.45rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #6b7280;
  transition: all 0.15s;
}

.icon-btn:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.icon-btn.danger {
  color: #ef4444;
  border-color: #fca5a5;
}

.icon-btn.danger:hover {
  background-color: #fef2f2;
}
</style>
